import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShieldCheck, Menu, X, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';

const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/features', label: 'Features' },
  { to: '/for-players', label: 'For Players' },
  { to: '/for-schools', label: 'For Schools' },
  { to: '/for-scouts', label: 'For Scouts' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/security-compliance', label: 'Security & Compliance' },
  { to: '/contact', label: 'Contact' },
];

export default function PublicNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <nav className="fixed top-0 w-full z-50 bg-black/85 backdrop-blur border-b border-zinc-900" data-testid="public-navbar">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-link">
            <img src="/logo.png" alt="FutureXV" className="h-10" />
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-5 text-sm flex-1 justify-center">
            {NAV_LINKS.map((l) => {
              const active = pathname === l.to;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase().replace(/\s|&/g, '-').replace(/-+/g,'-')}`}
                  className={`relative px-1 py-2 transition-colors ${active ? 'text-[#A3FF00]' : 'text-zinc-300 hover:text-white'}`}
                >
                  {l.label}
                  {active && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#A3FF00] rounded-full" />}
                </Link>
              );
            })}
          </div>

          <div className="hidden lg:flex items-center gap-2 shrink-0">
            <Button onClick={() => navigate('/auth?admin=true')} variant="ghost" className="text-zinc-300 hover:text-white gap-2" data-testid="nav-admin-button">
              <ShieldCheck className="w-4 h-4" />Admin
            </Button>
            <Button onClick={() => navigate('/auth')} variant="outline" className="border-zinc-700 text-zinc-200 hover:text-white hover:border-[#A3FF00] gap-2 px-5" data-testid="nav-login-button">
              <LogIn className="w-4 h-4" />Login
            </Button>
            <Button onClick={() => navigate('/auth?mode=signup')} className="bg-[#A3FF00] hover:bg-[#88dd00] text-black font-bold px-6" data-testid="nav-get-started">
              Get Started
            </Button>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden text-white p-2"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile drawer */}
        {open && (
          <div className="lg:hidden border-t border-zinc-900 py-4 space-y-1" data-testid="mobile-nav-drawer">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={`block px-3 py-2.5 rounded-md text-sm ${pathname === l.to ? 'bg-[#A3FF00]/10 text-[#A3FF00]' : 'text-zinc-300 hover:bg-zinc-900'}`}
              >
                {l.label}
              </Link>
            ))}
            <div className="pt-3 px-3 flex flex-col gap-2">
              <Button onClick={() => { setOpen(false); navigate('/auth?admin=true'); }} variant="outline" className="border-zinc-700 text-zinc-200 w-full">
                <ShieldCheck className="w-4 h-4 mr-2" />Admin
              </Button>
              <Button onClick={() => { setOpen(false); navigate('/auth'); }} variant="outline" className="border-zinc-700 text-zinc-200 w-full" data-testid="mobile-nav-login-button">
                <LogIn className="w-4 h-4 mr-2" />Login
              </Button>
              <Button onClick={() => { setOpen(false); navigate('/auth?mode=signup'); }} className="bg-[#A3FF00] hover:bg-[#88dd00] text-black font-bold w-full">
                Get Started
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
